import React, { useContext, useState, useEffect } from "react"
import { Box, Button, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Loading } from "../../components/Loading"
import ShiftSelection from "../../components/ShiftSelection"
import { UserContext } from "../../contexts/UserContext"
import DateCalendar from "../../components/DateCalendar"
import dayjs, { Dayjs } from "dayjs"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { IShift } from "../../entities/users/interface"

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const UserLounge = () => {
    const { t } = useTranslation()
    const userContext = useContext(UserContext)
    const navigate = useNavigate()
    const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"))
    const [shifts, setShifts] = useState<IShift[]>([])
    const [isTodayShift, setIsTodayShift] = useState(true)

    useEffect(() => {
        if (!userContext?.user?.shifts) {
            setShifts([])
            setIsTodayShift(true)
            return
        }
        const dayStart = dayjs(selectedDate).startOf("day")
        const dayEnd = dayjs(selectedDate).endOf("day")
        const filteredShifts = userContext.user.shifts.filter((shift) => {
            const shiftStart = dayjs(shift.shiftStart)
            return shiftStart.isSameOrAfter(dayStart) && shiftStart.isBefore(dayEnd)
        })
        setShifts(filteredShifts)

        // Check if all shifts are scheduled for today or nor
        const todayDate = dayjs().format("YYYY-MM-DD")
        const allShiftsToday = todayDate === selectedDate
        setIsTodayShift(allShiftsToday)
    }, [selectedDate, userContext?.user?.shifts])

    const handleStartNewShift = () => navigate(`/sites`)
    const handleDateChange = (date: Dayjs) => setSelectedDate(date.format("YYYY-MM-DD"))

    if (userContext?.userLoading) {
        return <Loading />
    } else if (userContext?.error && !userContext?.user) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 2 }}>
                <Typography variant="body1">{t("users.noUserFound")}</Typography>
                {userContext?.error && (
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                        {userContext.error}
                    </Typography>
                )}
            </Box>
        )
    } else if (!userContext?.user) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 2 }}>
                <Typography variant="body1">{t("users.noUser")}</Typography>
            </Box>
        )
    }

    return (
        <>
            <DateCalendar startDate={dayjs()} onDateChange={handleDateChange} />
            <Box
                sx={{
                    padding: 2,
                    marginBottom: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <Typography variant="h5" color="text.primary">
                    {t("shifts.mobile.title")}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    sx={{ minWidth: "120px" }}
                    onClick={handleStartNewShift}
                >
                    {t("shifts.mobile.buttons.new")}
                </Button>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                {shifts && shifts.length > 0 ? (
                    shifts.map((shift, index) => {
                        const shiftStatus =
                            Array.isArray(shift.shiftHistory) && shift.shiftHistory.length > 0
                                ? shift.shiftHistory[shift.shiftHistory.length - 1].status
                                : ""
                        return (
                            <ShiftSelection
                                key={index}
                                shift={shift}
                                isTodayShift={isTodayShift}
                                shiftStatus={shiftStatus}
                            />
                        )
                    })
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                        {t("shifts.mobile.details.noShiftAvailable")}
                    </Typography>
                )}
            </Box>
        </>
    )
}
export default UserLounge
